import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Star } from "lucide-react";

/**
 * Cinematic, Claude-inspired hero for the public landing page.
 *
 * - Calm warm gradient backdrop (paper → very-soft-clay-blush) with a
 *   subtle radial highlight behind the headline.
 * - Bold serif tagline (Instrument Serif) in a confident two-line setting.
 * - Embedded inline AI-tutor demo: a rounded card showing a real
 *   user → assistant exchange that types out in sequence on mount.
 * - Social proof row: rating, study count, curator count.
 *
 * Designed to land in the same slot as the previous compact hero so
 * the rest of the page (study modes scroll, features, popular sets,
 * etc.) flows below unchanged.
 */

const DEMO_LOOPS = [
  {
    id: "pythagoras",
    label: "Maths · Year 7",
    user: "Explain Pythagoras like I'm 12.",
    assistant:
      "Sure — picture a right triangle. The two short sides squared, added together, equal the long side squared. So a² + b² = c². It's how we know the diagonal of a 3 × 4 rectangle is exactly 5.",
  },
  {
    id: "biology-quiz",
    label: "Biology · GCSE",
    user: "Quiz me on cell division — three quick questions.",
    assistant:
      "Got it. 1) What's the key difference between mitosis and meiosis? 2) Which phase has chromosomes lined up at the equator? 3) What happens during crossing over? Take your time — I'll grade gently and explain whatever you miss.",
  },
  {
    id: "history-plan",
    label: "AP US History",
    user: "Write a study plan for AP US History — I have 4 weeks.",
    assistant:
      "Here's a 4-week sprint. Week 1 — Founding Era + Constitution. Week 2 — Antebellum & Civil War. Week 3 — Gilded Age → New Deal. Week 4 — Cold War + practice DBQs. I'll quiz you twice a week and adapt as we go.",
  },
  {
    id: "calc-integration",
    label: "Calculus · AP",
    user: "Explain integration by parts in plain English.",
    assistant:
      "It's the product rule, run backwards. ∫u dv = uv − ∫v du. Pick u so its derivative is simpler, dv so it's easy to integrate. The classic mnemonic is LIATE — log, inverse-trig, algebraic, trig, exponential — try that order for u.",
  },
];

const TypewriterLine = ({ text, speed = 14, onDone }) => {
  const [out, setOut] = useState("");
  useEffect(() => {
    setOut("");
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(id);
  }, [text, speed, onDone]);
  return <span>{out}</span>;
};

// Per-loop animation: user message slides in, then assistant typewrites.
const DemoSlide = ({ loop }) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    setStep(0);
    const t = setTimeout(() => setStep(1), 700);
    return () => clearTimeout(t);
  }, [loop.id]);

  return (
    <motion.div
      key={loop.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-5"
    >
      {/* User message */}
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm leading-relaxed shadow-soft">
          {loop.user}
        </div>
      </div>

      {/* Assistant message */}
      <AnimatePresence mode="wait">
        {step >= 1 && (
          <motion.div
            key={`a-${loop.id}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="flex items-start gap-3"
          >
            <div className="shrink-0 w-9 h-9 rounded-full bg-secondary border border-border flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 max-w-[85%] rounded-2xl rounded-tl-sm bg-muted/60 border border-border px-4 py-3 text-sm leading-relaxed text-foreground">
              <TypewriterLine text={loop.assistant} />
              <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-foreground/40 animate-pulse" aria-hidden />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const InlineTutorDemo = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-advance — pause when user hovers/focuses the card so they can read.
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % DEMO_LOOPS.length);
    }, 8000);
    return () => clearInterval(id);
  }, [paused]);

  const loop = DEMO_LOOPS[idx];

  return (
    <div
      className="relative mx-auto w-full max-w-2xl rounded-3xl border border-border bg-card/95 backdrop-blur-sm shadow-elevated overflow-hidden"
      data-testid="hero-tutor-demo"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* fake titlebar */}
      <div className="flex items-center gap-2 border-b border-border px-5 py-3 bg-muted/40">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        </div>
        <div className="flex-1 text-center text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          <AnimatePresence mode="wait">
            <motion.span
              key={loop.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="inline-block"
            >
              Cognify · {loop.label}
            </motion.span>
          </AnimatePresence>
        </div>
        <div className="w-10" aria-hidden />
      </div>

      <div className="p-6 sm:p-8 min-h-[260px]">
        <AnimatePresence mode="wait">
          <DemoSlide loop={loop} />
        </AnimatePresence>

        {/* fake input + indicator dots */}
        <div className="mt-6 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 rounded-full border border-border bg-background/80 px-4 py-2 text-sm text-muted-foreground">
            <span className="opacity-70">Ask anything…</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider font-medium opacity-60">⌘K</span>
          </div>
          <div className="flex items-center gap-1.5" aria-label="Demo conversations">
            {DEMO_LOOPS.map((d, i) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ease-out-expo ${
                  i === idx ? "w-6 bg-primary" : "w-1.5 bg-border hover:bg-foreground/30"
                }`}
                data-testid={`hero-demo-dot-${i}`}
                aria-label={`Show demo: ${d.label}`}
                aria-current={i === idx ? "true" : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CinematicHero = ({ onPrimary, onGoogle }) => {
  return (
    <section
      className="relative overflow-hidden pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20"
      data-testid="hero-section"
    >
      {/* Calm gradient backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 90% 60% at 50% 12%, rgba(108,60,225,0.10), transparent 70%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(245,158,11,0.08), transparent 70%), linear-gradient(to bottom, hsl(var(--background)) 0%, hsl(var(--muted)) 100%)",
        }}
      />
      {/* Subtle grain texture overlay */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.035] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-6"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 backdrop-blur-sm px-3.5 py-1.5 text-xs font-medium text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            New · Personalised AI tutoring for every subject
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="font-heading text-center text-foreground tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl"
        >
          Learn anything,
          <br className="hidden sm:block" />
          <span className="italic text-primary"> intelligently.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-6 mx-auto max-w-2xl text-center text-base sm:text-lg text-muted-foreground leading-relaxed"
        >
          A calm, focused AI tutor that explains concepts in your voice,
          builds personalised study paths, and remembers what you struggle with —
          so you actually understand, not just memorise.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Button
            onClick={onPrimary}
            size="lg"
            className="w-full sm:w-auto h-12 px-7 text-base shadow-soft hover:shadow-card-hover"
            data-testid="hero-get-started-btn"
          >
            Get started free
            <ArrowRight className="ml-1 w-4 h-4" />
          </Button>
          <Button
            onClick={onGoogle}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto h-12 px-7 text-base"
            data-testid="hero-google-auth-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </Button>
        </motion.div>

        {/* Inline tutor demo */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 sm:mt-16"
        >
          <InlineTutorDemo />
        </motion.div>

        {/* Social proof row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-sm text-muted-foreground"
          data-testid="hero-social-proof"
        >
          <div className="flex items-center gap-2">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span><strong className="text-foreground">4.9</strong> from 2,400+ students</span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-border" />
          <div>
            <strong className="text-foreground">200,000+</strong> study sessions guided
          </div>
          <div className="hidden sm:block w-px h-5 bg-border" />
          <div>
            Trusted by educators in <strong className="text-foreground">38 countries</strong>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CinematicHero;
